<template>
    <DsCard class="decision-panel">
        <div class="header-content">
            <div class="header-text">
                <span class="header-card-title">
                    {{ headerTitle }}
                </span>
                <span class="header-card-body">
                    {{ headerDescription }}
                </span>
            </div>
            <DecisionDiamondCardMenu
                @import-rules-from-menu="enableImportRulesFromMenu"
                @delete-all-menu="enableDeleteAllMenu"
            />
        </div>
        <section class="separator" />
        <section
            v-for="group in localRuleGroups"
            :key="group && group.id"
        >
            <DecisionDiamondRuleValues
                :comparison="group && group.comparison"
                :rules-map="group && group.rulesMap"
                :dd-keys="ddKeys"
                @remove-rule="ruleId => removeRow(ruleId)"
                @add-rule-value-global="updateRuleWithLocalRuleGroups"
                @duplicate-row="duplicateRow"
            />

            <DecisionDiamondComparison
                :rule-group="group"
                :num-rule-groups="localRuleGroups.length"
                @add-new-rule-map="addingNewRuleMap"
            />
        </section>

        <div class="add-rule-button-wrapper" @click="addingRuleGroup">
            <DsIcon name="add-fill" class="add-button-icon" />
            <span class="add-button-label">{{ $t('funnel.edit.decision.addRule') }}</span>
        </div>

        <div v-if="showNoRulesAlert" class="no-rules-alert">
            <DsInlineAlert leading-icon type="subtle">
                {{ $t('funnel.edit.decision.default.title') }}
            </DsInlineAlert>
        </div>

        <DecisionImportRulesModal
            v-if="showImportRulesModal"
            :is-open="showImportRulesModal"
            :title-sequence-original="ruleGroup.title"
            :dd-keys="ddKeys"
            :target-rule-group-id="ruleGroup.id"
            :flow-ids="decisions.flowIds"
            :flow-names="decisions.flowNames"
            :decision-ids="decisions.decisionIds"
            @import-rules="importRules"
            @cancel-import-rules="cancelImportRules"
        />

        <DsConfirmDialog
            v-if="showDeleteAllDialog"
            :is-open="showDeleteAllDialog"
            :message="message"
            destructive
            :confirm-label="$t('funnel.edit.decision.delete.rules.confirmation.button')"
            :cancel-label="$t('funnel.edit.decision.delete.rules.cancel.button')"
            @cancel="closeConfirmDialog"
            @confirm="deleteAllRules"
        />
    </DsCard>
</template>
<script>
import {
    addRule,
    addRuleGroup,
    deleteRule,
    duplicateRule,
    sequenceDeleteAllRules,
} from './decision.api';
import DecisionDiamondCardMenu from './DecisionDiamondCardMenu.vue';
import DecisionDiamondRuleValues from './DecisionDiamondRuleValues.vue';
import DecisionDiamondComparison from './DecisionDiamondComparison.vue';
import DecisionImportRulesModal from './DecisionImportRulesModal.vue';

export default {
    name: 'DecisionDiamondRuleGroup',
    components: {
        DecisionDiamondCardMenu,
        DecisionDiamondRuleValues,
        DecisionDiamondComparison,
        DecisionImportRulesModal,
    },
    props: {
        decisions: {
            type: Object,
            required: true,
        },
        ruleGroup: {
            type: Object,
            required: true,
        },
        ddKeys: {
            type: Object,
            required: true,
            default: () => ({
                primaryKey: 'Marketing',
                secondaryKey: '',
                secondaryKeyId: 0,
            }),
        },
    },
    emits: ['local-rule-groups-update'],
    data() {
        return {
            localRuleGroups: [],
            primaryKey: this.ddKeys.primaryKey,
            secondaryKey: this.ddKeys.secondaryKey,
            secondaryKeyId: this.ddKeys.secondaryKeyId,
            showImportRulesModal: false,
            showDeleteAllDialog: false,
        };
    },
    computed: {
        showNoRulesAlert() {
            return this.localRuleGroups.length === 0;
        },
        headerTitle() {
            return this.$t('funnel.edit.decision.rulesFor', { title: this.ruleGroup.title });
        },
        headerDescription() {
            return this.$t('funnel.edit.decision.rulesDescription', { sequenceName: this.ruleGroup.title });
        },
        message() {
            return this.$t('funnel.edit.decision.delete.rules.confirmation.message', { sequence_name: this.ruleGroup.title });
        },
    },
    watch: {
        ruleGroup: {
            deep: true,
            handler(newVal) {
                this.processSequence(newVal);
            },
        },
    },
    mounted() {
        const ruleGroupMapObject = this.ruleGroup.ruleGroupMap;

        this.localRuleGroups = Object.keys(ruleGroupMapObject).map(
            (key) => ({ ...ruleGroupMapObject[key] }),
        );
    },
    methods: {
        importRules(ruleGroup) {
            const { ruleGroupMap } = ruleGroup;
            const ruleGroups = Object.values(ruleGroupMap);

            this.mergeRuleInLocalRuleGroups(ruleGroups);
            this.localRuleGroupsUpdateToParent();
        },
        cancelImportRules() {
            this.showImportRulesModal = false;
        },
        enableImportRulesFromMenu() {
            this.showImportRulesModal = true;
        },
        enableDeleteAllMenu() {
            this.showDeleteAllDialog = true;
        },
        closeConfirmDialog() {
            this.showDeleteAllDialog = false;
        },
        async deleteAllRules() {
            const ruleGroupId = this.ruleGroup.id;
            const { ruleGroup } = await sequenceDeleteAllRules(ruleGroupId, this.ddKeys);

            if (ruleGroup) {
                this.localRuleGroups = [];
            }

            this.closeConfirmDialog();
            this.localRuleGroupsUpdateToParent();
        },
        processSequence(newSequence) {
            const ruleGroupMapObject = newSequence.ruleGroupMap;
            const clonedSequence = JSON.parse(JSON.stringify(ruleGroupMapObject));

            this.localRuleGroups = Object.keys(clonedSequence).map((key) => clonedSequence[key]);
        },
        async addingRuleGroup() {
            try {
                const payload = {
                    ruleGroupId: this.ruleGroup.id,
                    ...this.ddKeys,
                };

                const { ruleGroup: { ruleGroupMap } } = await addRuleGroup(payload);

                const ruleGroups = Object.values(ruleGroupMap);

                this.mergeRuleInLocalRuleGroups(ruleGroups);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                this.localRuleGroupsUpdateToParent();
            }
        },
        localRuleGroupsUpdateToParent() {
            this.$emit('local-rule-groups-update', {
                ruleGroupId: this.ruleGroup.id,
                hasRules: this.localRuleGroups.length > 0,
                localRuleGroups: this.localRuleGroups,
            });
        },

        async addingNewRuleMap(targetRuleGroupId) {
            const payload = {
                ruleGroupId: targetRuleGroupId,
                ...this.ddKeys,
            };

            const { ruleGroup: { rulesMap } } = await addRule(payload);

            this.localRuleGroups = this.localRuleGroups.map((localRuleGroup) => {
                if (localRuleGroup.id === targetRuleGroupId) {
                    const updatedRulesMap = { ...localRuleGroup.rulesMap };

                    Object.entries(rulesMap).forEach(([newRuleId, newRule]) => {
                        if (!Object.prototype.hasOwnProperty.call(updatedRulesMap, newRuleId)) {
                            this.$set(updatedRulesMap, newRuleId, newRule);
                        }
                    });

                    return { ...localRuleGroup, rulesMap: updatedRulesMap };
                }

                return localRuleGroup;
            });
        },
        mergeRuleInLocalRuleGroups(ruleGroupsAdded) {
            const existingRulesMapIds = new Set();

            this.localRuleGroups.forEach((ruleGroup) => {
                Object.keys(ruleGroup.rulesMap).forEach((ruleId) => {
                    existingRulesMapIds.add(parseInt(ruleId, 10));
                });
            });

            ruleGroupsAdded.forEach((addedRuleGroup) => {
                Object.values(addedRuleGroup.rulesMap).forEach((addedRule) => {
                    if (!existingRulesMapIds.has(addedRule.id)) {
                        const targetRuleGroupIndex = this.localRuleGroups.findIndex((rg) => rg.id === addedRuleGroup.id);

                        if (targetRuleGroupIndex !== -1) {
                            this.localRuleGroups[targetRuleGroupIndex].rulesMap[addedRule.id] = {
                                ruleGroupId: addedRule.ruleGroupId,
                                order: addedRule.order,
                                subject: addedRule.subject,
                                category: addedRule.category,
                                field: addedRule.field,
                                constraint: addedRule.constraint,
                                comparison: addedRule.comparison,
                                valueMap: Object.values(addedRule.valueMap).reduce((vm, rv) => {
                                    vm[rv.id] = {
                                        id: rv.id,
                                        ruleId: rv.ruleId,
                                        order: rv.order,
                                        value: rv.value,
                                    };

                                    return vm;
                                }, {}),
                            };
                        } else {
                            const newRuleMap = {
                                [addedRule.id]: {
                                    ruleGroupId: addedRuleGroup.id,
                                    order: addedRule.order,
                                    subject: addedRule.subject,
                                    category: addedRule.category,
                                    field: addedRule.field,
                                    constraint: addedRule.constraint,
                                    comparison: addedRule.comparison,
                                    valueMap: Object.values(addedRule.valueMap).reduce((vm, rv) => {
                                        vm[rv.id] = {
                                            id: rv.id,
                                            ruleId: rv.ruleId,
                                            order: rv.order,
                                            value: rv.value,
                                        };

                                        return vm;
                                    }, {}),
                                },
                            };

                            const newRuleGroup = {
                                id: addedRuleGroup.id,
                                order: addedRuleGroup.order,
                                comparison: addedRuleGroup.comparison,
                                rulesMap: newRuleMap,
                            };

                            this.localRuleGroups.push(newRuleGroup);
                        }
                    }
                });
            });
        },
        async duplicateRow(ruleMapKey, ruleGroupInnerId) {
            const ruleId = ruleMapKey;

            const { ruleGroup: ruleGroupInner } = await duplicateRule(ruleId, ruleGroupInnerId, this.ddKeys);

            this.localRuleGroups = this.localRuleGroups.map((ruleGroup) => {
                if (ruleGroup.id === ruleGroupInner.id) {
                    return ruleGroupInner;
                }

                return ruleGroup;
            });
            this.localRuleGroupsUpdateToParent();
        },
        updateRuleWithLocalRuleGroups({ ruleMapKey, rule }) {
            this.localRuleGroups = this.localRuleGroups.map((ruleGroup) => {
                if (Object.prototype.hasOwnProperty.call(ruleGroup.rulesMap, ruleMapKey)) {
                    return {
                        ...ruleGroup,
                        rulesMap: {
                            ...ruleGroup.rulesMap,
                            [ruleMapKey]: rule,
                        },
                    };
                }

                return ruleGroup;
            });
        },
        async removeRow(ruleIdToRemove) {
            try {
                this.localRuleGroups = this.localRuleGroups.reduce((acc, ruleGroup) => {
                    const newRulesMap = Object.entries(ruleGroup.rulesMap)
                        .filter(([ruleId]) => ruleId !== ruleIdToRemove)
                        .reduce((newMap, [ruleId, ruleValue]) => ({ ...newMap, [ruleId]: ruleValue }), {});

                    if (Object.keys(newRulesMap).length > 0) {
                        acc.push({ ...ruleGroup, rulesMap: newRulesMap });
                    }

                    return acc;
                }, []);

                await deleteRule(ruleIdToRemove);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                this.localRuleGroupsUpdateToParent();
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.decision-panel {
    width: 100%;
    max-width: px-to-rem(1440);
    height: auto;
    min-height: px-to-rem(182);
    flex-shrink: 0;
    border-radius: px-to-rem(12);
    padding-bottom: $spacing-200;
    background: $color-paper;
    margin: 0 auto $spacing-200 auto;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-text {
    display: flex;
    flex-direction: column;
}

.header-card-title {
    display: flex;
    font-style: normal;
    line-height: $line-height-lg;
    font-size: $font-size-lg;
    font-weight: 500;
    font-family: $headline-font-family;
    margin-top: $spacing-200;
    margin-left: $spacing-250;
}

.header-card-body {
    display: flex;
    flex-direction: column;
    line-height: $line-height-sm;
    font-size: $font-size-sm;
    margin-left: $spacing-250;
    margin-bottom: $spacing-200;
    font-weight: $font-weight-regular;
    font-family: $headline-font-family;
}

.separator {
    border-bottom: solid px-to-rem(1) $color-gray-200;
}

.plus-label-and {
    color: $color-blue;
    font-weight: bold;
}

.add-rule-button-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $spacing-100;
    cursor: pointer;
    margin-top: px-to-rem(20);
    padding-left: $spacing-250;
}

.add-button-icon {
    flex: 0 0 auto;
    color: $color-blue;
    margin: 0;
}

.add-button-label {
    color: $color-blue;
    font-weight: 700;
}

.no-rules-alert {
    margin-top: $spacing-250;
    margin-left: $spacing-250;
}

</style>
